import { FormValueControl } from '@angular/forms/signals';
import { Directive, input, model } from '@angular/core';
import { FormControlStateAbstract } from './form-control-state-abstract';

/**
 * Base directive for every Juno control that edits a **value**: text, number,
 * email, search, select, and radio groups.
 *
 * All validation-state and presentational inputs live on
 * {@link FormControlStateAbstract}. This class adds only what the
 * `FormValueControl` contract requires on top of that:
 * - a two-way `value` model signal, driven by `[formField]` or a standalone
 *   `[(value)]` binding.
 * - `placeholder`, which is meaningless for a checkbox and so does not belong
 *   on the shared base.
 *
 * A control on this contract **must not** declare a `checked` member:
 * `FormValueControl` types it as `checked?: undefined`, and `[formField]` uses
 * its presence to decide the control is a checkbox.
 *
 * @template T  The value type held by this control.
 *              - `JunoInput`  → `string | number`
 *              - `JunoSelect` → `string | string[]` (single / multi mode)
 *
 * @template CL The shape of the optional `appliedClasses` object.
 *              - `JunoInput`  → `string`
 *              - `JunoSelect` → `{ selectedClasses: string; optionClasses: string }`
 *
 * @implements {FormValueControl<T | undefined>}
 *
 * @usageNotes
 * Concrete subclasses only need to:
 * 1. Extend `InputValueClassAbstract<T, CL>`.
 * 2. Bind `[value]` / `[(value)]` in their template.
 * 3. Emit `touch` on the relevant blur event.
 *
 * ```ts
 * export class MyControl extends InputValueClassAbstract<string, string> {
 *   // add control-specific inputs here
 * }
 * ```
 *
 * In a template, the control is used either with Signal Forms:
 * ```html
 * <my-control [formField]="myForm.firstName" label="First name" />
 * ```
 * or as a standalone two-way binding:
 * ```html
 * <my-control [(value)]="nameSignal" label="First name" />
 * ```
 *
 * @see {@link FormControlStateAbstract} for the inherited state inputs.
 * @see {@link FormCheckClassAbstract} for the checkbox counterpart.
 */
@Directive()
export abstract class FormValueClassAbstract<T, CL = unknown>
  extends FormControlStateAbstract<CL>
  implements FormValueControl<T | undefined>
{
  value = model<T>();
  placeholder = input<string>('');
}
